import { createRouter, createWebHistory } from 'vue-router'
import { toast } from 'vue-sonner'
import { BILLING_LOCATION, RouteName } from '@/app/route-names'
import { confirmCheckout, forgetCheckout } from '@/features/billing/utils/pending-checkout'
import { ensureCurrentUser } from '@/features/auth/queries/ensureCurrentUser'
import { ensureMyOrganizations } from '@/features/organizations/queries/ensureMyOrganizations'
import { i18n } from '@/i18n'
import { can } from '@/lib/permissions/can'
import { Permission, type PermissionKey } from '@/lib/permissions/keys'
import { useAuthStore } from '@/stores/auth'

const PUBLIC_PATHS = ['/welcome', '/login', '/sso/callback', '/invitations/accept']

const GUEST_ONLY_PATHS = ['/welcome', '/login']

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: [
    { path: '/', redirect: () => (useAuthStore().isAuthenticated ? '/dashboard' : '/welcome') },
    {
      path: '/welcome',
      component: () => import('@/features/auth/pages/WelcomePage.vue'),
      meta: { layout: 'blank' },
    },
    {
      path: '/login',
      component: () => import('@/features/auth/pages/LoginPage.vue'),
      meta: { layout: 'blank' },
    },
    {
      path: '/sso/callback',
      component: () => import('@/features/sso/pages/SsoCallbackPage.vue'),
      meta: { layout: 'blank' },
    },
    {
      // Invitation links; works signed in (accept) and signed out (sign in or sign up).
      path: '/invitations/accept',
      component: () => import('@/features/invitations/pages/AcceptInvitationPage.vue'),
      meta: { layout: 'blank' },
    },
    {
      path: '/dashboard',
      component: () => import('@/features/dashboard/pages/DashboardPage.vue'),
    },
    {
      path: '/profile',
      component: () => import('@/features/profile/pages/ProfilePage.vue'),
    },
    {
      path: '/notifications',
      component: () => import('@/features/notifications/pages/NotificationsPage.vue'),
    },
    {
      path: '/merchants',
      component: () => import('@/features/merchants/pages/MerchantsPage.vue'),
      meta: { permission: Permission.MerchantList },
    },
    {
      path: '/merchants/new',
      name: RouteName.AddMerchant,
      component: () => import('@/features/merchants/pages/AddMerchantPage.vue'),
      meta: { permission: Permission.MerchantCreate },
    },
    {
      path: '/websites',
      component: () => import('@/features/websites/pages/WebsitesPage.vue'),
      meta: { permission: Permission.WebsiteList },
    },
    {
      path: '/websites/requests/new',
      component: () => import('@/features/websites/pages/CreateWebsiteRequestPage.vue'),
      meta: { permission: Permission.WebsiteCreate },
    },
    {
      path: '/websites/:id(\\d+)',
      name: RouteName.WebsiteRequest,
      component: () => import('@/features/websites/pages/WebsiteRequestPage.vue'),
      meta: { permission: Permission.WebsiteList },
    },
    {
      // Billing moved into organization settings; keeps bookmarks and external links working.
      path: '/billing',
      redirect: BILLING_LOCATION,
    },
    {
      // Where the provider's hosted checkout returns to (`config/billing.php` on the backend).
      path: '/billing/success',
      redirect: () => {
        // The subscription is recorded by the provider's webhook, so it can show up a moment later.
        confirmCheckout()
        toast.success(i18n.global.t('billing.toast.checkoutCompleted'))
        return BILLING_LOCATION
      },
    },
    {
      path: '/billing/cancel',
      redirect: () => {
        forgetCheckout()
        return BILLING_LOCATION
      },
    },
    {
      path: '/settings',
      component: () => import('@/features/settings/pages/SettingsPage.vue'),
    },
    {
      path: '/organization/settings',
      component: () => import('@/features/organizations/pages/OrganizationSettingsPage.vue'),
    },
    {
      path: '/system-admin',
      component: () => import('@/features/system-admin/pages/SystemAdminPage.vue'),
      meta: { systemAdminOnly: true },
    },
    {
      path: '/system-admin/websites/:id/request',
      component: () => import('@/features/websites/pages/EditWebsiteRequestPage.vue'),
      meta: { systemAdminOnly: true },
    },
  ],
})

router.beforeEach(async (to) => {
  const { isAuthenticated } = useAuthStore()

  if (!PUBLIC_PATHS.includes(to.path) && !isAuthenticated) return '/login'
  if (GUEST_ONLY_PATHS.includes(to.path) && isAuthenticated) return '/dashboard'

  if (to.meta.systemAdminOnly) {
    await ensureCurrentUser().catch(() => undefined)

    if (!useAuthStore().user?.isSystemAdmin) {
      toast.error(i18n.global.t('common.noPermission'))
      return '/dashboard'
    }
  }

  const permission = to.meta.permission as PermissionKey | undefined

  if (permission) {
    // Permissions come from the API; load them first so a hard refresh doesn't bounce.
    await ensureMyOrganizations().catch(() => undefined)

    if (!can(permission)) {
      toast.error(i18n.global.t('common.noPermission'))
      return '/dashboard'
    }
  }

  return true
})

export default router
